'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { mtc } from '@/styles'
import { Select } from '@/ui'
import { matchesOptionSearch } from '../../../utils/option-search'

export interface SearchableSelectOption<T extends string> {
  value: T | number
  label: string
  searchText?: string
  key?: string
  onClick?: () => void
  isTaxCompliant?: boolean
}

export interface SearchableSelectProps<T extends string> {
  options: SearchableSelectOption<T>[]
  value: T | undefined
  onChange: (value: T | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  onBlur?: () => void
  variant?: 'default' | 'text'
  inputRef?: React.RefObject<HTMLInputElement>
}

export const SearchableSelect = <T extends string>({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled,
  className,
  onBlur,
  inputRef,
}: SearchableSelectProps<T>) => {
  const [searchValue, setSearchValue] = useState('')
  const [open, setOpen] = useState(false)
  // No option is ever the focused element, so the highlight needs state.
  const [activeIndex, setActiveIndex] = useState(0)
  // Selecting an option closes the dropdown and returns focus to this input,
  // which would otherwise immediately reopen it via onFocus below — this
  // flag skips exactly that one reopen, not any other focus event.
  const justSelected = useRef(false)
  const optionRefs = useRef<(HTMLDivElement | null)[]>([])
  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      matchesOptionSearch(searchValue, option.label, option.searchText)
    )
  }, [options, searchValue])

  const selected = value
    ? options.find((option) => String(option.value) === String(value))
    : undefined

  // `nearest`: only scroll an option that is actually out of view.
  useEffect(() => {
    if (!open) return
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  const selectOption = (option: SearchableSelectOption<T>): void => {
    onChange(option.value as T)
    setOpen(false)
    justSelected.current = true
    option.onClick?.()
    setTimeout(() => {
      setSearchValue('')
      // Only the refocus this selection triggers should be swallowed.
      // Without this the flag stays armed whenever that refocus never
      // lands, eating a later keyboard focus instead.
      justSelected.current = false
    }, 100)
  }

  /**
   * Radix's own key handling assumes the trigger holds focus; here the search
   * input inside it does, so none of it runs. `stopPropagation` keeps the
   * trigger underneath from also acting on a claimed key.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // Otherwise the caret jumps to either end of the search text.
      e.preventDefault()
      e.stopPropagation()
      if (!open) {
        setOpen(true)
        setActiveIndex(0)
        return
      }
      if (filteredOptions.length === 0) return
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActiveIndex(
        (i) => (i + delta + filteredOptions.length) % filteredOptions.length
      )
      return
    }

    if (e.key === 'Enter') {
      if (!open) return
      // A bare Enter would submit the surrounding form, empty results
      // included, so the key is claimed whenever the list is open.
      e.preventDefault()
      e.stopPropagation()
      const option = filteredOptions[activeIndex]
      if (option) selectOption(option)
      return
    }

    if (e.key === 'Escape' && open) {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <Select
      open={open}
      disabled={disabled}
      onBlur={onBlur}
      value={value ?? ''}
      searchable
    >
      <Select.Trigger
        onClick={() => setOpen((prev) => !prev)}
        className={mtc(
          'searchable-select-trigger min-h-[48px] h-auto justify-start',
          className
        )}
      >
        <div className="flex flex-1 ml-2 flex-wrap gap-y-2 items-center justify-start">
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            className="min-w-[100px] flex-1"
          >
            <input
              placeholder={selected ? selected.label : placeholder}
              value={searchValue}
              ref={inputRef}
              onFocus={() => {
                if (justSelected.current) {
                  justSelected.current = false
                  return
                }
                setOpen(true)
              }}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setOpen(true)
                setActiveIndex(0)
              }}
              onClick={(e) => {
                e.currentTarget.focus()
                setOpen(true)
              }}
              onBlur={(e) => {
                if (
                  e.relatedTarget?.classList.contains(
                    'searchable-select-content'
                  ) ||
                  e.relatedTarget?.classList.contains(
                    'searchable-select-option'
                  ) ||
                  e.relatedTarget?.classList.contains(
                    'searchable-select-trigger'
                  )
                ) {
                  e.currentTarget.focus()
                } else {
                  setSearchValue('')
                  onBlur?.()
                }
              }}
              className={`w-full h-[30px] outline-none text-sm bg-transparent text-left ${
                selected ? 'placeholder-neutral-900' : 'placeholder-neutral-400'
              }`}
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content
        onPointerDownOutside={() => setOpen(false)}
        className="searchable-select-content"
      >
        {filteredOptions.length === 0 ? (
          <div className="text-sm text-neutral-500 px-4 py-2">
            No options found
          </div>
        ) : (
          filteredOptions.map((option, index) => (
            <Select.Item
              key={option.key || option.value}
              ref={(el: HTMLDivElement | null) => {
                optionRefs.current[index] = el
              }}
              value={String(option.value)}
              // The atom's own hover colour, so arrowing and mousing match.
              className={mtc(
                'searchable-select-option',
                index === activeIndex && 'bg-neutral-25'
              )}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectOption(option)}
            >
              {option.label}
            </Select.Item>
          ))
        )}
      </Select.Content>
    </Select>
  )
}
